<template>
  <div class="eval-grid-management">
    <div class="eval-grid-management__header">
      <div>{{ $t('evaluation-grid.management.field-name') }}</div>
      <div>{{ $t('evaluation-grid.management.field-values') }}</div>
    </div>
    <div class="eval-grid-management__content">
      <div v-for="(field, index) in evaluationFields" :key="index" class="eval-grid-management__field-container">
        <div class="eval-grid-management__field">
          <div class="eval-grid-management__name">{{ field.name }}</div>
          <div v-if="field.choices.length > 0" class="eval-grid-management__choices">
            <div v-for="(choice, idx) in field.choices" :key="idx" class="eval-grid-management__choice">
              {{ choice }}
            </div>
          </div>
          <div v-else class="eval-grid-management__selection-type">
            {{ $t('evaluation-grid.management.free-text') }}
          </div>
        </div>
        <BaseRoundButton
          :size="32"
          icon="trash-2"
          :tooltip-text="$t('actions.delete')"
          is-transparent
          @click.native="openDeleteFieldDialog(field)"
          class="eval-grid-management__delete"
        />
      </div>
      <BaseAppendButton @execute="openAddFieldForm" has-icon-left class="eval-grid-management__add">
        <BaseIcon name="plus" />
        {{ $t('evaluation-grid.add-field.add-new-field') }}
      </BaseAppendButton>
      <div class="eval-grid-management__add-field-section" v-if="addFieldForm">
        <EvaluationGridFieldForm
          ref="addFieldForm"
          :evaluation-grid-fields="evaluationFields"
          @close="closeAddFieldForm"
          @save="executeAddField"
        />
      </div>
    </div>
    <div class="dialog__warning-notice">
      {{ $t('evaluation-grid.management.locale-copy-warning') }}
    </div>
    <ConfirmationDialog ref="deleteFieldDialog" :buttonText="$t('actions.delete')" @execute="executeDeleteField">
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="evaluation-grid.delete-field">
            <template v-slot:action>
              <span class="confirmation-dialog__action">{{ $t('actions.delete') }}</span>
            </template>
            <template v-slot:fieldName v-if="manipulatedObject">
              <span class="confirmation-dialog__user">{{ manipulatedObject.name }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import EvaluationGridFieldForm from '@/components/page-pdf-analysis/EvaluationGridFieldForm.vue';
import BaseAppendButton from '@/components/base/BaseAppendButton.vue';
import BaseIcon from '@/components/base/BaseIcon';
import BaseRoundButton from '@/components/base/BaseRoundButton';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'EvaluationGrid',
  components: { EvaluationGridFieldForm, BaseAppendButton, BaseIcon, BaseRoundButton, ConfirmationDialog },
  props: {},
  data() {
    return {
      manipulatedObject: null,
      addFieldForm: false,
    };
  },
  computed: {
    ...mapGetters('evaluationGrid', ['evaluationGrid', 'evaluationFields']),
    ...mapGetters('opportunity', ['opportunity']),
  },
  methods: {
    ...mapActions('evaluationGrid', ['addEvalGridField', 'deleteEvalGridField', 'getEvaluationGrid']),
    openAddFieldForm() {
      this.addFieldForm = true;
      this.$nextTick(() => {
        const refInput = this.$refs.addFieldForm.$refs.fieldNameInput.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });
    },
    closeAddFieldForm() {
      if (this.$refs.addFieldForm) this.$refs.addFieldForm.resetForm();
      this.addFieldForm = false;
    },
    openDeleteFieldDialog(field) {
      this.manipulatedObject = field;
      this.$refs.deleteFieldDialog.openDialog();
    },
    executeDeleteField() {
      this.deleteEvalGridField({
        opportunityId: this.opportunity.id,
        fieldId: this.manipulatedObject.id,
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
          this.getEvaluationGrid(this.opportunity.id);
        })
        .catch((error) => this.$toast.error(error));
      this.manipulatedObject = null;
    },
    executeAddField(field) {
      this.addEvalGridField({
        opportunityId: this.opportunity.id,
        field: field,
      })
        .then(() => {
          this.getEvaluationGrid(this.opportunity.id);
          this.$toast.success(this.$t('toast.success'));
        })
        .catch((error) => this.$toast.error(error));
    },
  },
};
</script>

<style lang="scss">
.eval-grid-management {
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    justify-content: space-between;
    font-size: 14px;
    padding: 0px 42px 16px 0px;
    font-weight: bold;
  }

  &__content {
    overflow-y: auto;
    max-height: 50%;
  }

  &__field-container {
    display: flex;
    align-items: center;
    background: $color-neutral-pale;
    margin-bottom: 8px;
  }

  &__delete {
    .base-icon {
      color: $color-error;
    }
  }

  &__add-field-section {
    margin-top: -2px;
    border: 8px solid $color-neutral-pale;
    border-top: transparent;
    border-radius: 0 0 $border-radius-medium $border-radius-medium;
    padding: 16px;
  }

  &__field {
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
    padding: 8px;
    border-radius: $border-radius-medium;
  }

  &__name {
    color: $color-normal-pale;
    font-size: 16px;
  }

  &__choices {
    justify-content: flex-end;
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__choice {
    background-color: $color-light-grey;
    padding: 4px 8px;
    font-size: 14px;
    border-radius: $border-radius-small;
    max-width: 180px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &__selection-type {
    font-size: 14px;
    color: $color-neutral;
  }
}
</style>
